import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, Box, Skeleton } from '@mui/material';
import {
    CellClassParams,
    ColDef,
    ProcessCellForExportParams,
    ValueFormatterParams,
} from 'ag-grid-community';

import { styled } from '@mui/material/styles';
import {
    getExcelFilename,
    MULTI_YEAR_COMPARISON_PREFIX,
} from 'routes/BudgetModule/contexts/helpers';
import AgGridReactExposingApi from 'routes/BudgetModule/components/shared/AgGridReactExposingAPI';
import { getInfoIconTemplate } from 'helpers/iconHelper';
import { currencyFormatter, percentageFormatter } from 'helpers/formatHelper';
import { MultiYearsComparisonRow } from 'routes/BudgetModule/components/BudgetOverview/BudgetSynthesisAndRatios/MultiYearsComparison/types';
import {
    BUDGET_EXCEL_EXPORT_PARAMS,
    BUDGET_EXCEL_STYLES,
    CURRENCY_EXCEL_CLASSNAMES,
    PERCENTAGE_EXCEL_CLASSNAMES,
} from 'routes/BudgetModule/config/excelExportHelpers';
import MultiYearsAmountCellRenderer from 'routes/BudgetModule/components/BudgetOverview/BudgetSynthesisAndRatios/MultiYearsComparison/components/MultiYearsAmountCellRenderer';
import IndicatorNameCellRenderer from 'routes/BudgetModule/components/BudgetOverview/BudgetSynthesisAndRatios/MultiYearsComparison/components/IndicatorNameCellRenderer';
import _ from 'lodash';
import { translateComponent } from 'i18n';

type Props = {
    year: number;
    data: MultiYearsComparisonRow[];
    isLoading: boolean;
    phase: string | undefined;
};

const StyGridContainer = styled(Box, {
    name: 'StyGridContainer',
})({
    marginTop: '2rem',
    height: '18rem',
    '&& .ag-root-wrapper': {
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
    },
});

const getCurrentYearTooltip = (phase: string | undefined) => {
    if (phase === undefined || phase === 'mandate') return 'Montant mandaté';
    if (phase === 'total') return 'Montant budgété total';
    if (phase === 'proposal') return 'Montant proposé';
    if (phase === 'arbitrage') return 'Montant arbitré';
    if (phase === 'voté') return 'Montant primitif voté';
    return undefined;
};

const valueFormatter = (
    params: ValueFormatterParams<
        MultiYearsComparisonRow,
        MultiYearsComparisonRow[number]
    >,
) => {
    const amount = params.value?.value;
    const rowIndex = params.node?.rowIndex;
    if (rowIndex == null || amount == null) return '';
    return rowIndex > 2
        ? percentageFormatter(amount, { showSign: true })
        : currencyFormatter(amount, { compact: true });
};

const isExcelValueType = (
    value: MultiYearsComparisonRow[number] | number,
): value is number => typeof value === 'number';

/**
 * Warning: this function does not take the same 'params' when the grid is initialized (to process
 * the cellClass for potential CSS rules) and when the grid is exporting to excel (cellClass is re-used
 * by AgGrid to allow us to tweak the Excel cell formatting). In the latter case,
 * the params.value is a number and not an object because `processCellCallback` is called before, and it
 * returns the final displayed value for the Excel cell.
 * @returns
 */
const getCellClass = (
    params: CellClassParams<
        MultiYearsComparisonRow,
        MultiYearsComparisonRow[number] | number
    >,
) => {
    if (!params.value) return '';
    if (!isExcelValueType(params.value)) return '';
    const { value } = params;

    const dataPoint = _.find(
        params.data,
        (d: MultiYearsComparisonRow[number]) => {
            return d.value === value;
        },
    ) as MultiYearsComparisonRow[number];
    // this logic is specific to this table, where we mix different types of data
    // in the same column.
    return dataPoint?.type === 'percentage'
        ? PERCENTAGE_EXCEL_CLASSNAMES
        : CURRENCY_EXCEL_CLASSNAMES;
};

const excelValueFormatter = (
    params: ProcessCellForExportParams<MultiYearsComparisonRow>,
) => {
    return params.value.value;
};

const DEFAULT_COL_DEF: ColDef = {
    flex: 1,
    minWidth: 80,
    sortable: false,
    suppressMovable: true,
};

const COMPONENTS = {
    multiYearsCellRenderer: MultiYearsAmountCellRenderer,
    indicatorNameCellRenderer: IndicatorNameCellRenderer,
};

const getNumericColDef = (primaryColor: string): ColDef => ({
    cellRenderer: 'multiYearsCellRenderer',
    headerComponentParams: {
        template: getInfoIconTemplate(primaryColor),
    },
    valueFormatter,
    cellClass: getCellClass,
    context: {
        excelValueFormatter,
    },
    headerTooltip: translateComponent('budgetOverview.mandate'),
    type: 'numericColumn',
});

export default function MultiYearsComparisonGrid({
    year,
    data,
    isLoading,
    phase,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const theme = useTheme();

    const primaryColor = theme.palette.primary.main;

    const numericColDef = useMemo(
        () => getNumericColDef(primaryColor),
        [primaryColor],
    );

    const colDefs = useMemo<ColDef[]>(
        () => [
            {
                field: 'name',
                headerName: t('header.indicators'),
                cellRenderer: 'indicatorNameCellRenderer',
                cellRendererParams: { colored: true },
                minWidth: 200,
            },
            {
                ...numericColDef,
                field: `${year - 3}`,
            },
            {
                ...numericColDef,
                field: `${year - 2}`,
            },
            {
                ...numericColDef,
                field: `${year - 1}`,
            },
            {
                ...numericColDef,
                field: `${year}`,
                cellRendererParams: { colored: true },
                headerTooltip: getCurrentYearTooltip(phase),
            },
        ],
        [numericColDef, phase, t, year],
    );

    return (
        <StyGridContainer
            id="budget-overview-table"
            className="ag-theme-material"
        >
            {isLoading ? (
                <Skeleton variant="rectangular" sx={{ height: 288 }} />
            ) : (
                <AgGridReactExposingApi
                    rowData={data}
                    rowHeight={36}
                    headerHeight={40}
                    columnDefs={colDefs}
                    defaultColDef={DEFAULT_COL_DEF}
                    tooltipShowDelay={0}
                    tooltipHideDelay={2000}
                    components={COMPONENTS}
                    excelExport={{
                        fileName: getExcelFilename(
                            MULTI_YEAR_COMPARISON_PREFIX,
                        ),
                        sheetName: MULTI_YEAR_COMPARISON_PREFIX,
                    }}
                    excelStyles={BUDGET_EXCEL_STYLES}
                    defaultExcelExportParams={BUDGET_EXCEL_EXPORT_PARAMS}
                />
            )}
        </StyGridContainer>
    );
}
